/* eslint-disable react-hooks/exhaustive-deps */
/**
 *
 * IndividualDashboard - Professional Document Library
 *
 */
import { LayoutWrapper } from 'app/components/LayoutWrapper/Loadable';
import { useLayoutSlice } from 'app/components/LayoutWrapper/slice';
import { selectLayout } from 'app/components/LayoutWrapper/slice/selectors';
import React, { useEffect, useState, useRef } from 'react';

import Moment from 'react-moment';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { routes } from 'utils/routes';
import { useIndividualDashboardSlice } from './slice';
import { selectIndividualDashboard } from './slice/selectors';
import './styles.scss';
import { useQuery } from 'utils/commons';
import { toast } from 'react-toast';
import { DeleteAgreementConfirmModal } from 'app/components/DeleteAgreementConfirmModal';
import { useViewDocumentSlice } from '../ViewDocument/slice';
import { selectViewDocument } from '../ViewDocument/slice/selectors';
import { TableLoader } from '../../components/TableLoader/Loadable';

interface Props {}

const individualFoldersList = [
  {
    id: 0,
    name: 'In Progress',
    folder: 'inProgress',
    icon: '📝',
    description: 'Documents awaiting signatures',
  },
  {
    id: 1,
    name: 'All Documents',
    folder: 'all',
    icon: '📄',
    description: 'Complete document library',
  },
];
let startTour = id => {
  window.USETIFUL?.tour?.start(id);
};

const templateMenu = [
  { value: 'download', label: 'Download' },
  { value: 'delete', label: 'Delete' },
];

const folderIcon = 'fi fi-rr-folder s15pt';

export function IndividualDashboard(props: Props) {
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [
    isDeleteConfirmModalVisible,
    setIsDeleteConfirmModalVisible,
  ] = useState(false);
  const [deleteSelectedDoc, setDeleteSelectedDoc] = useState<any>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterOptions, setFilterOptions] = useState<any>({
    drafter: '',
    status: [],
  });
  const history = useHistory();
  const doClickRow = (row, event) => {
    // Check if document is truly an individual document or a company document where user is recipient
    if (row.isIndividualDocument) {
      history.push(routes.viewSelfDocument(row.agrUID));
    } else {
      // For company documents where user is recipient, use individual document viewer route
      history.push(
        routes.viewIndividualDocument(row?.company?.companyId, row.agrUID),
      );
    }
  };
  const { actions: layoutActions } = useLayoutSlice();
  const { actions } = useIndividualDashboardSlice();
  const dashboardState = useSelector(selectIndividualDashboard);
  const layoutState = useSelector(selectLayout);

  const { actions: viewDocActions } = useViewDocumentSlice();
  const viewDocumentState = useSelector(selectViewDocument);

  const dispatch = useDispatch();

  const params = useQuery();

  useEffect(() => {
    dispatch(layoutActions.setSelectedCompany({ company: null }));
    dispatch(layoutActions.setSelectedFolder(individualFoldersList[0])); // Set In Progress as default

    const isSandboxLink = params.get('sandboxLink');

    if (isSandboxLink) {
      window.USETIFUL?.tour?.start(32544);
    }

    // Mobile detection with resize listener
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      dispatch(layoutActions.resetSelectedFolder());
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (layoutState.user) {
      fetchIndAgreements();
    }
    // else {
    //   history.push(routes.login);
    // }
  }, [layoutState.user]);

  useEffect(() => {
    if (dashboardState.documents !== null) {
      setIsInitialLoad(false);
    }
  }, [dashboardState.documents]);

  useEffect(() => {
    dispatch(
      actions.changeSelectedFolderDocuments(layoutState.selectedFolder.folder),
    );
  }, [layoutState.selectedFolder]);

  const fetchIndAgreements = () => {
    dispatch(
      actions.getDocuments({
        userId: layoutState.user._id,
        selectedFolder: layoutState.selectedFolder.folder,
      }),
    );
  };

  const onSelectFolder = folder => {
    // console.log(folder);
    dispatch(layoutActions.setSelectedFolder(folder));
    // console.log(layoutState.selectedFolder);
  };

  const onDeleteClick = row => {
    if (row?.agreement?.agreementStatus !== 'deleted') {
      setDeleteSelectedDoc(row);
      setIsDeleteConfirmModalVisible(true);
    } else if (
      row?.agreement?.agreementStatus === 'deleted' &&
      layoutState.userRoles?.includes('Admin')
    ) {
      setDeleteSelectedDoc(row);
      setIsDeleteConfirmModalVisible(true);
    } else {
      toast.warn('This agreement cannot be deleted');
    }
  };

  const onDeleteDocumentConfirm = () => {
    const request = {
      ...deleteSelectedDoc,
      agreement: {
        ...deleteSelectedDoc.agreement,
        agreementStatus: 'deleted',
      },
      userEmail: layoutState.user.email,
    };

    dispatch(
      viewDocActions.updateDocument({
        request,
        type: 'delete',
        successCb: () => {
          setIsDeleteConfirmModalVisible(false);

          setDeleteSelectedDoc(null);
          fetchIndAgreements();
        },
      }),
    );
  };

  const filteredAgreements =
    dashboardState.documents && !isInitialLoad
      ? dashboardState.documents
          ?.slice()
          .sort((agr1, agr2) => {
            const firstDate = new Date(agr1.dates.draftDate).getTime();
            const secondDate = new Date(agr2.dates.draftDate).getTime();

            return sortBy === 'oldest'
              ? firstDate - secondDate
              : secondDate - firstDate;
          })
          .filter(
            agreement =>
              agreement &&
              agreement.agreement &&
              agreement.agreement.agreementTitle &&
              (agreement.agreement.agreementTitle
                .split(' ')
                .map(name => name.toLowerCase())
                .findIndex(name =>
                  name.toLowerCase().startsWith(filterText.toLowerCase()),
                ) > -1 ||
                (agreement.addedParties || [])
                  .reduce(
                    (parties, party) => [
                      ...parties,
                      (party.name || '').toLowerCase(),
                    ],
                    [],
                  )
                  .findIndex(name =>
                    name.startsWith(filterText.toLowerCase()),
                  ) > -1) &&
              (!filterOptions.drafter ||
                (agreement.drafter &&
                  agreement.drafter.userName &&
                  agreement.drafter.userName
                    .toLowerCase()
                    .startsWith(filterOptions.drafter))) &&
              (filterOptions.status?.length === 0 ||
                filterOptions.status.includes(agreement.userStatus)),
          )
      : [];
  // console.log(filteredAgreements);

  const handleBulkAction = (action: string) => {
    if (selectedDocuments.length === 0) {
      toast.warn('Please select documents first');
      return;
    }

    switch (action) {
      case 'download':
        selectedDocuments.forEach(docId => {
          const doc = filteredAgreements.find(d => d.agrUID === docId);
          if (doc?.urls?.agreementUrl || doc?.urls?.draftUrl) {
            window.open(doc.urls.agreementUrl || doc.urls.draftUrl, '_blank');
          }
        });
        break;
      case 'delete':
        // Handle bulk delete
        break;
    }
  };

  const handleDocumentSelect = (docId: string, selected: boolean) => {
    if (selected) {
      setSelectedDocuments(prev => [...prev, docId]);
    } else {
      setSelectedDocuments(prev => prev.filter(id => id !== docId));
    }
  };

  const handleEmailDocument = (doc: any) => {
    const email = prompt('Enter recipient email:');
    if (email && email.includes('@')) {
      // API call to email document
      fetch('/api/documents/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: doc.agrUID,
          recipientEmail: email,
          message: `Document: ${doc.agreement.agreementTitle}`,
        }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            toast.success('Document emailed successfully!');
          } else {
            toast.error('Failed to email document');
          }
        })
        .catch(() => toast.error('Failed to email document'));
    } else {
      toast.warn('Please enter a valid email address');
    }
  };

  return (
    <>
      <LayoutWrapper title="Document Library">
        <div className="professional-dashboard">
          {/* Header Section */}
          <div className="dashboard-header">
            <div className="header-content">
              <div className="header-left">
                <div className="title-with-info">
                  <h1 className="dashboard-title">My Personal Library</h1>
                  <button
                    className="info-btn"
                    onClick={() => setShowInfoModal(true)}
                    title="Learn about Individual Dashboard"
                  >
                    <i className="fi fi-rr-info"></i>
                  </button>
                </div>
                <p className="dashboard-subtitle">
                  {filteredAgreements.length} documents •{' '}
                  {layoutState.selectedFolder.name}
                </p>
              </div>
              <div className="header-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowUploadModal(true)}
                >
                  <i className="fi fi-rr-cloud-upload"></i>
                  Upload Document
                </button>
              </div>
            </div>

            {/* Upgrade Prompt */}
            <div className="upgrade-prompt-header">
              <div className="upgrade-card">
                <div className="upgrade-content">
                  <div className="upgrade-text">
                    <h3>Need More Power?</h3>
                    <p>
                      Create a Company Workspace to send documents for signing,
                      team collaboration, templates, and more.
                    </p>
                  </div>
                  <button
                    className="btn btn-primary upgrade-btn"
                    onClick={() => history.push(routes.createCompany)}
                  >
                    {/* <i className="fi fi-rr-galaxy-star"></i> */}
                    Upgrade Now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation & Controls */}
          <div className="dashboard-nav">
            <div className="nav-content">
              <div className="folder-tabs">
                {individualFoldersList.map(folder => (
                  <button
                    key={folder.id}
                    className={`folder-tab ${
                      layoutState.selectedFolder.name === folder.name
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => onSelectFolder(folder)}
                    title={folder.description}
                  >
                    {/* <span className="tab-icon">{folder.icon}</span> */}
                    <span className="tab-label">{folder.name}</span>
                  </button>
                ))}
              </div>

              <div className="view-controls">
                <div className="search-container">
                  <div className="search-input-wrapper">
                    <svg
                      className="search-icon"
                      width={isMobile ? '18' : '16'}
                      height={isMobile ? '18' : '16'}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                    <input
                      type="text"
                      className="search-input"
                      placeholder={
                        isMobile ? 'Search...' : 'Search documents...'
                      }
                      value={filterText}
                      onChange={e => setFilterText(e.target.value)}
                      autoComplete="off"
                      autoCapitalize="none"
                      autoCorrect="off"
                    />
                  </div>
                </div>

                <div className="control-group">
                  <select
                    className="sort-select"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="name">Name A-Z</option>
                    <option value="status">By Status</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedDocuments.length > 0 && (
              <div className="bulk-actions-bar">
                <div className="bulk-info">
                  <span className="selected-count">
                    {selectedDocuments.length} selected
                  </span>
                  <button
                    className="clear-selection"
                    onClick={() => setSelectedDocuments([])}
                  >
                    Clear
                  </button>
                </div>
                <div className="bulk-actions">
                  <button
                    className="bulk-btn"
                    onClick={() => handleBulkAction('download')}
                    style={{ touchAction: 'manipulation' }}
                  >
                    <svg
                      width={isMobile ? '18' : '16'}
                      height={isMobile ? '18' : '16'}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                    </svg>
                    Download
                  </button>
                  <button
                    className="bulk-btn danger"
                    onClick={() => handleBulkAction('delete')}
                    style={{ touchAction: 'manipulation' }}
                  >
                    <svg
                      width={isMobile ? '18' : '16'}
                      height={isMobile ? '18' : '16'}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* Documents Container */}
            <div className="documents-container">
              {dashboardState.isGettingUserDocuments || isInitialLoad ? (
                <TableLoader target="documents" />
              ) : filteredAgreements.length > 0 ? (
                <div className="documents-grid list">
                  {filteredAgreements.map(doc => (
                    <div
                      key={doc.agrUID}
                      className={`dashboard-document-card ${
                        selectedDocuments.includes(doc.agrUID) ? 'selected' : ''
                      }`}
                    >
                      <div className="card-header">
                        <label className="document-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedDocuments.includes(doc.agrUID)}
                            onChange={e =>
                              handleDocumentSelect(doc.agrUID, e.target.checked)
                            }
                            onClick={e => e.stopPropagation()}
                          />
                          <span className="checkmark"></span>
                        </label>
                        <div className="document-status-badge">
                          {(() => {
                            const status = doc.userStatus?.toLowerCase();
                            const agreementStatus = doc.agreement?.agreementStatus?.toLowerCase();

                            if (
                              status?.includes('ready') ||
                              status?.includes('awaiting')
                            ) {
                              return <span className="status-dot ready"></span>;
                            }
                            if (
                              status?.includes('binding') ||
                              status?.includes('completed') ||
                              agreementStatus === 'binding'
                            ) {
                              return (
                                <span className="status-dot complete"></span>
                              );
                            }
                            if (
                              agreementStatus === 'drafted' ||
                              status?.includes('draft')
                            ) {
                              return <span className="status-dot draft"></span>;
                            }
                            return <span className="status-dot draft"></span>;
                          })()}
                        </div>
                      </div>

                      <div
                        className="card-content"
                        onClick={() => doClickRow(doc)}
                      >
                        <div className="document-info">
                          <div className="document-header">
                            <h3
                              className="document-title"
                              title={doc.agreement.agreementTitle}
                            >
                              {doc.agreement.agreementTitle}
                            </h3>
                          </div>

                          <div className="document-details">
                            {doc.drafter &&
                              doc.drafter.userName !==
                                layoutState.user?.firstName +
                                  ' ' +
                                  layoutState.user?.lastName && (
                                <div className="detail-row">
                                  <span className="detail-value">
                                    <span className="smallText">From:</span>
                                    {doc?.company?.companyName}
                                  </span>
                                </div>
                              )}

                            <div className="detail-row">
                              {(() => {
                                const agreementStatus = doc.agreement?.agreementStatus?.toLowerCase();
                                const userStatus = doc.userStatus;

                                let displayStatus = 'Draft';
                                let statusClass = 'draft';
                                let statusIcon = '✏️';

                                if (agreementStatus === 'drafted') {
                                  displayStatus = 'Draft';
                                  statusClass = 'draft';
                                  statusIcon = '✏️';
                                } else if (
                                  userStatus?.toLowerCase().includes('ready') ||
                                  userStatus?.toLowerCase().includes('awaiting')
                                ) {
                                  displayStatus = 'Ready to Sign';
                                  statusClass = 'ready';
                                  statusIcon = '✍️';
                                } else if (
                                  agreementStatus === 'binding' ||
                                  userStatus
                                    ?.toLowerCase()
                                    .includes('completed')
                                ) {
                                  displayStatus = 'Completed';
                                  statusClass = 'completed';
                                  statusIcon = '✅';
                                } else if (userStatus) {
                                  displayStatus = userStatus.replace('_', ' ');
                                  statusClass = userStatus
                                    .replace(/\s+/g, '-')
                                    .toLowerCase();
                                  statusIcon = '⏳';
                                }

                                return (
                                  <span
                                    className={`detail-value status-container status-${statusClass}`}
                                  >
                                    <span className="status-text">
                                      {displayStatus}
                                    </span>
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className="card-actions"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="list-actions">
                          {doc?.userStatus !== 'Completed' &&
                            doc?.agreement?.agreementStatus !== 'binding' && (
                              <button
                                className="primaryButton"
                                onClick={() => doClickRow(doc)}
                                title="Sign"
                                style={{ touchAction: 'manipulation' }}
                              >
                                Sign
                              </button>
                            )}
                          <div className="dropdown">
                            <button
                              className="dropdown-btn"
                              style={{ touchAction: 'manipulation' }}
                              aria-label="More actions"
                            >
                              <svg
                                width={isMobile ? '18' : '16'}
                                height={isMobile ? '18' : '16'}
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                              </svg>
                            </button>
                            <div className="dropdown-menu">
                              <button
                                className="dropdown-item"
                                onClick={() =>
                                  window.open(
                                    doc.urls?.agreementUrl ||
                                      doc.urls?.draftUrl,
                                    '_blank',
                                  )
                                }
                                style={{ touchAction: 'manipulation' }}
                              >
                                📥 Download
                              </button>
                              <button
                                className="dropdown-item"
                                onClick={() => handleEmailDocument(doc)}
                                style={{ touchAction: 'manipulation' }}
                              >
                                📧 Email
                              </button>
                              <button
                                className="dropdown-item danger"
                                onClick={() => onDeleteClick(doc)}
                                style={{ touchAction: 'manipulation' }}
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-illustration">
                    <svg
                      width="120"
                      height="120"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      opacity="0.3"
                    >
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  </div>
                  <h3 className="empty-title">No Documents Found</h3>
                  <p className="empty-description">
                    {filterText
                      ? `No documents match "${filterText}"`
                      : 'Upload your first document to get started with your personal library'}
                  </p>
                  <div className="empty-actions">
                    {filterText ? (
                      <button
                        className="btn btn-secondary"
                        onClick={() => setFilterText('')}
                      >
                        Clear Search
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowUploadModal(true)}
                      >
                        <span className="btn-icon">📤</span>
                        Upload Document
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {showUploadModal && (
            <div
              className="upload-modal-overlay"
              onClick={() => setShowUploadModal(false)}
            >
              <div className="upload-modal" onClick={e => e.stopPropagation()}>
                <div className="upload-header">
                  <h3>Upload Document</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowUploadModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="upload-content">
                  {!uploadedFile ? (
                    <div className="upload-dropzone">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setUploadPreview(URL.createObjectURL(file));
                          }
                        }}
                        style={{ display: 'none' }}
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="upload-area">
                        <div className="upload-icon">📄</div>
                        <p>Click to upload or drag and drop</p>
                        <span>PDF files only</span>
                      </label>
                    </div>
                  ) : (
                    <div className="upload-preview">
                      <div className="preview-header">
                        <span>📄 {uploadedFile.name}</span>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            setUploadPreview('');
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div className="preview-actions">
                        <button
                          className="cancel-btn"
                          onClick={() => setShowUploadModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="upload-confirm-btn"
                          onClick={() => {
                            if (uploadedFile) {
                              // Create form data using same logic as sidebar
                              const myName = layoutState.user
                                ? layoutState.user.firstName +
                                  ' ' +
                                  layoutState.user.lastName
                                : '';

                              const formData = new FormData();
                              formData.append('file', uploadedFile);
                              formData.append(
                                'agreement.agreementStatus',
                                'drafted',
                              );
                              formData.append(
                                'agreement.agreementDescription',
                                '',
                              );
                              formData.append('drafter.userName', myName);
                              formData.append(
                                'drafter.userId',
                                layoutState.user?._id,
                              );
                              formData.append(
                                'drafter.userEmail',
                                layoutState.user?.email,
                              );
                              formData.append('tags', '[]');
                              formData.append('isIndividualDocument', 'true');
                              formData.append(
                                'recipients',
                                JSON.stringify([
                                  {
                                    id: layoutState.user?._id,
                                    name: myName,
                                    title: myName,
                                    email: layoutState.user?.email,
                                    initial: myName[0],
                                    memberId: null,
                                    contactId: null,
                                    entity: 'individual',
                                    accessKey: null,
                                    companySigner: null,
                                    companySignerName: null,
                                    isGuestFlowCompleted: false,
                                  },
                                ]),
                              );
                              formData.append(
                                'addedParties',
                                JSON.stringify([
                                  {
                                    id: layoutState.user?._id,
                                    name: myName,
                                    title: myName,
                                    email: layoutState.user?.email,
                                    initial: myName[0],
                                    memberId: null,
                                    contactId: null,
                                    entity: 'individual',
                                    accessKey: null,
                                    companySigner: null,
                                    companySignerName: null,
                                    isGuestFlowCompleted: false,
                                    color: '#6ECE99',
                                    status: 'pending',
                                    userType: 'Recipient',
                                    contactType: 'individual',
                                  },
                                ]),
                              );

                              // Use the layout actions for upload
                              dispatch(
                                layoutActions.uploadDoc({
                                  form: formData,
                                  setProgress: progress => {
                                    // Progress tracking for upload
                                  },
                                  successCb: docId => {
                                    setShowUploadModal(false);
                                    setUploadedFile(null);
                                    setUploadPreview('');
                                    fetchIndAgreements();
                                    toast.success(
                                      'Document uploaded successfully!',
                                    );
                                    history.push(
                                      routes.viewSelfDocument(docId),
                                    );
                                  },
                                  errorCb: () => {
                                    toast.error('Failed to upload document');
                                  },
                                }),
                              );
                            }
                          }}
                        >
                          Upload Document
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {showInfoModal && (
            <div
              className="info-modal-overlay"
              onClick={() => setShowInfoModal(false)}
            >
              <div className="info-modal" onClick={e => e.stopPropagation()}>
                <div className="info-header">
                  <h3>Your Personal Library</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowInfoModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="info-content">
                  <div className="welcome-hero">
                    <p>
                      A free space to manage your personal documents with
                      professional-grade security and e-signature capabilities.
                    </p>
                  </div>

                  <div className="upgrade-cta">
                    <div className="cta-content">
                      <h3>Need Team Features?</h3>
                      <p>
                        Get unlimited storage, team collaboration, templates,
                        and advanced workflows.
                      </p>
                      <div className="cta-benefits">
                        <span>👥 Team Collaboration</span>
                        <span>📋 Document Templates</span>
                        <span>⚡ Advanced Workflows</span>
                        <span>☁️ Unlimited Storage</span>
                      </div>
                    </div>
                    <button
                      className="upgrade-btn-new"
                      onClick={() => {
                        setShowInfoModal(false);
                        history.push(routes.createCompany);
                      }}
                    >
                      Upgrade to Company
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isDeleteConfirmModalVisible && (
            <DeleteAgreementConfirmModal
              isOpen={isDeleteConfirmModalVisible}
              closeModal={() => setIsDeleteConfirmModalVisible(false)}
              onDeleteAgreementConfirm={onDeleteDocumentConfirm}
              isDeletingDoc={viewDocumentState.isDeletingDoc}
            />
          )}
        </div>
      </LayoutWrapper>
    </>
  );
}
